import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, FileText, Plus, Check, X, Clock, DollarSign,
  Calendar, AlertCircle, Loader2, ChevronRight, Sparkles
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: "Pendente", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejeitado", color: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelado", color: "bg-neutral-100 text-neutral-700" }
};

const REASON_OPTIONS = [
  { value: "scope_change", label: "Mudança de Escopo" },
  { value: "unforeseen", label: "Imprevisto na Obra" },
  { value: "client_request", label: "Solicitação do Cliente" },
  { value: "material_change", label: "Troca de Material" },
  { value: "design_change", label: "Alteração de Projeto" },
  { value: "other", label: "Outro" }
];

export default function ProjectAddendumPage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [addendums, setAddendums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const [formData, setFormData] = useState({
    reason: "",
    description: "",
    cost_delta: "",
    time_delta: "",
    scope_changes: ""
  });

  const [aiAnalysis, setAiAnalysis] = useState(null);

  useEffect(() => {
    fetchAddendums();
  }, [projectId, token]);

  const fetchAddendums = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/addendums`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setAddendums(await res.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeImpact = async () => {
    if (!formData.description.trim()) {
      toast.error("Descreva a mudança solicitada");
      return;
    }

    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/suggest`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: `addendum_${projectId}_${Date.now()}`,
          message: `Analise o impacto desta mudança em um projeto de construção: ${formData.description}. 
          Forneça: 1) Estimativa de custo adicional em R$, 2) Impacto no prazo em dias, 3) Riscos potenciais, 4) Recomendação.`,
          context: { type: "addendum_analysis" }
        })
      });

      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data.response);
      }
    } catch (error) {
      toast.error("Erro ao analisar impacto");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.reason || !formData.description.trim()) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/addendums`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          reason: formData.reason,
          description: formData.description,
          changes: {
            cost_delta: parseFloat(formData.cost_delta) || 0,
            time_delta: parseInt(formData.time_delta) || 0,
            scope_changes: formData.scope_changes
          },
          ai_impact_analysis: aiAnalysis ? { raw_response: aiAnalysis } : null
        })
      });

      if (res.ok) {
        toast.success("Aditivo solicitado!");
        setShowForm(false);
        setFormData({ reason: "", description: "", cost_delta: "", time_delta: "", scope_changes: "" });
        setAiAnalysis(null);
        fetchAddendums();
      } else {
        toast.error("Erro ao solicitar aditivo");
      }
    } catch (error) {
      toast.error("Erro ao solicitar aditivo");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (addendumId) => {
    try {
      const res = await fetch(`${API_URL}/api/addendums/${addendumId}/approve`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.ok) {
        toast.success("Aditivo aprovado!");
        fetchAddendums();
      }
    } catch (error) {
      toast.error("Erro ao aprovar aditivo");
    }
  };

  const handleReject = async (addendumId) => {
    try {
      const res = await fetch(`${API_URL}/api/addendums/${addendumId}/reject`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.ok) {
        toast.success("Aditivo rejeitado");
        fetchAddendums();
      }
    } catch (error) {
      toast.error("Erro ao rejeitar aditivo");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  const pendingAddendums = addendums.filter(a => a.status === "pending");
  const resolvedAddendums = addendums.filter(a => a.status !== "pending");

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="project-addendum-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(`/project/${projectId}`)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Aditivos de Contrato</h1>
            <p className="text-xs text-neutral-500">{addendums.length} aditivos</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="btn-minimal btn-primary h-9 px-4 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Solicitar
          </Button>
        </div>
      </header>

      <main className="px-5 py-6 space-y-6">
        {/* New Addendum Form */}
        {showForm && (
          <div className="card-clean p-5 animate-up" data-testid="addendum-form">
            <h3 className="font-semibold text-neutral-900 mb-4">Solicitar Aditivo</h3>
            
            <div className="space-y-4">
              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Motivo *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className="input-minimal"
                  data-testid="reason-select"
                >
                  <option value="">Selecione o motivo</option>
                  {REASON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Descrição *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva detalhadamente a mudança solicitada..."
                  className="input-minimal h-24 resize-none py-3"
                  data-testid="description-input"
                />
              </div>

              {/* Cost & Time Impact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Impacto no Custo (R$)
                  </label>
                  <input
                    type="number"
                    value={formData.cost_delta}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost_delta: e.target.value }))}
                    placeholder="+/- 0,00"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Impacto no Prazo (dias)
                  </label>
                  <input
                    type="number"
                    value={formData.time_delta}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_delta: e.target.value }))}
                    placeholder="+/- 0"
                    className="input-minimal"
                  />
                </div>
              </div>

              {/* AI Analysis Button */}
              <Button
                onClick={handleAnalyzeImpact}
                disabled={analyzing || !formData.description.trim()}
                className="btn-minimal btn-secondary w-full"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Analisar Impacto com IA
              </Button>

              {/* AI Analysis Result */}
              {aiAnalysis && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Análise de Impacto</span>
                  </div>
                  <p className="text-sm text-blue-700 whitespace-pre-line">{aiAnalysis}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ reason: "", description: "", cost_delta: "", time_delta: "", scope_changes: "" });
                    setAiAnalysis(null);
                  }}
                  className="btn-minimal btn-secondary flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="btn-minimal btn-primary flex-1"
                  data-testid="submit-addendum-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Solicitar"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Addendums */}
        {pendingAddendums.length > 0 && (
          <div className="animate-up">
            <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Pendentes de Aprovação
            </h3>
            <div className="space-y-3">
              {pendingAddendums.map((addendum) => (
                <AddendumCard
                  key={addendum.addendum_id}
                  addendum={addendum}
                  user={user}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </div>
          </div>
        )}

        {/* Resolved Addendums */}
        {resolvedAddendums.length > 0 && (
          <div className="animate-up">
            <h3 className="font-semibold text-neutral-900 mb-4">Histórico</h3>
            <div className="space-y-3">
              {resolvedAddendums.map((addendum) => (
                <AddendumCard
                  key={addendum.addendum_id}
                  addendum={addendum}
                  user={user}
                  readonly
                />
              ))}
            </div>
          </div>
        )}

        {addendums.length === 0 && !showForm && (
          <div className="card-subtle p-8 text-center">
            <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="font-medium text-neutral-900">Nenhum aditivo</p>
            <p className="text-sm text-neutral-500 mt-1">
              Solicite um aditivo quando houver mudanças no escopo
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function AddendumCard({ addendum, user, onApprove, onReject, readonly = false }) {
  const status = STATUS_CONFIG[addendum.status] || STATUS_CONFIG.pending;
  const reasonLabel = REASON_OPTIONS.find(r => r.value === addendum.reason)?.label || addendum.reason;

  const canApprove = !readonly && addendum.status === "pending" && (
    (user?.role === "client" && !addendum.client_approved) ||
    (user?.role === "provider" && !addendum.provider_approved)
  );

  return (
    <div className="card-clean p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-xs text-neutral-500">{reasonLabel}</span>
          <p className="font-medium text-neutral-900 mt-1">{addendum.description}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
          {status.label}
        </span>
      </div>

      {/* Changes */}
      {addendum.changes && (
        <div className="flex items-center gap-4 text-sm text-neutral-600 mb-3">
          {addendum.changes.cost_delta !== 0 && (
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              <span className={addendum.changes.cost_delta > 0 ? "text-red-600" : "text-emerald-600"}>
                {addendum.changes.cost_delta > 0 ? "+" : ""}
                R$ {Math.abs(addendum.changes.cost_delta).toLocaleString("pt-BR")}
              </span>
            </div>
          )}
          {addendum.changes.time_delta !== 0 && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span className={addendum.changes.time_delta > 0 ? "text-red-600" : "text-emerald-600"}>
                {addendum.changes.time_delta > 0 ? "+" : ""}
                {addendum.changes.time_delta} dias
              </span>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis */}
      {addendum.ai_impact_analysis?.raw_response && (
        <div className="p-3 bg-blue-50 rounded-lg mb-3">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Análise IA</span>
          </div>
          <p className="text-xs text-blue-600 line-clamp-2">
            {addendum.ai_impact_analysis.raw_response}
          </p>
        </div>
      )}

      {/* Approval Status */}
      <div className="flex items-center gap-4 text-xs text-neutral-500 mb-3">
        <span className={addendum.client_approved ? "text-emerald-600" : ""}>
          {addendum.client_approved ? "✓ Cliente aprovou" : "Cliente: pendente"}
        </span>
        <span className={addendum.provider_approved ? "text-emerald-600" : ""}>
          {addendum.provider_approved ? "✓ Prestador aprovou" : "Prestador: pendente"}
        </span>
      </div>

      {/* Actions */}
      {canApprove && (
        <div className="flex gap-2 pt-3 border-t border-neutral-100">
          <Button
            onClick={() => onReject(addendum.addendum_id)}
            className="btn-minimal btn-ghost flex-1 h-9 text-red-600 hover:bg-red-50"
          >
            <X className="w-4 h-4 mr-1" />
            Rejeitar
          </Button>
          <Button
            onClick={() => onApprove(addendum.addendum_id)}
            className="btn-minimal btn-primary flex-1 h-9"
          >
            <Check className="w-4 h-4 mr-1" />
            Aprovar
          </Button>
        </div>
      )}

      {/* Date */}
      <p className="text-xs text-neutral-400 mt-3">
        {new Date(addendum.created_at).toLocaleDateString("pt-BR", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })}
      </p>
    </div>
  );
}
